import { Link } from "react-router-dom";
import { motion } from "framer-motion";

export default function Privacy() {
    return (
        <main className="bg-gradient-to-b from-slate-900 to-black text-gray-100 py-16 overflow-hidden">
            <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-start gap-12">

                {/* 🔹 Text Section */}
                <motion.div
                    className="flex-1 z-10"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: "easeOut" }}
                >
                    <h1 className="text-4xl sm:text-5xl font-bold text-white">
                        Privacy Policy
                    </h1>
                    <p className="mt-2 text-sm text-gray-400">
                        Last updated: {new Date().toLocaleDateString()}
                    </p>

                    <p className="mt-6 text-lg text-gray-300 leading-relaxed">
                        SecureID Technologies LLC (“SecureID”, “we”, “us”, or “our”) provides
                        identity and testing software solutions, including DactylScan Live Scan,
                        ExamsPro, and the Background Check Portal (collectively, the “Services”).
                        This Privacy Policy describes how we handle information across our website,
                        products, and customer environments.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">1) Roles</h2>
                    <ul className="mt-3 list-disc pl-6 text-gray-300 space-y-2">
                        <li>
                            <strong>Website & marketing:</strong> We act as an independent controller for
                            information submitted via our website.
                        </li>
                        <li>
                            <strong>Customer environments:</strong> For hosted or on-premise customer
                            environments, we act as a processor/service provider.
                        </li>
                    </ul>

                    <h2 className="mt-10 text-2xl font-semibold text-white">2) Information We Collect</h2>
                    <ul className="mt-3 list-disc pl-6 text-gray-300 space-y-2">
                        <li>Business contact data (name, email, employer, role)</li>
                        <li>Account and audit data (logins, activity logs, telemetry)</li>
                        <li>Support communications and attachments</li>
                        <li>Customer-submitted data such as identity or biometric records</li>
                        <li>Essential cookies and minimal analytics</li>
                    </ul>

                    <h2 className="mt-10 text-2xl font-semibold text-white">3) How We Use Information</h2>
                    <ul className="mt-3 list-disc pl-6 text-gray-300 space-y-2">
                        <li>Provide, secure, and improve the Services</li>
                        <li>Respond to inquiries and support requests</li>
                        <li>Maintain compliance and auditability</li>
                        <li>Comply with legal obligations and prevent misuse</li>
                    </ul>

                    <h2 className="mt-10 text-2xl font-semibold text-white">4) Biometric Data</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        Some deployments handle biometric data (e.g., fingerprints) under customer control.
                        SecureID supports customer-hosted or managed environments with configurable retention
                        and access policies, aligned with applicable privacy laws (e.g., BIPA, CUBI).
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">5) Legal Bases (GDPR/UK)</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        For website interactions we rely on legitimate interest and contract performance.
                        For customer data, we act as a processor under customer instructions.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">6) Sharing</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        We share limited data with service providers under strict agreements, and as required
                        by law. We do not sell personal data.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">7) Security & Retention</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        We employ encryption, access controls, and audit logs. Retention aligns with purpose,
                        customer instruction, and applicable law.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">8) Your Rights</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        Depending on your jurisdiction, you may request access, correction, deletion, or
                        portability. For data processed on behalf of a customer, contact that customer directly.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">9) Children</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        Our Services are designed for business and institutional use, not for children under 16.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">10) International Transfers</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        We implement appropriate safeguards, including Standard Contractual Clauses where applicable.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">11) Contact</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        SecureID Technologies LLC, Vero Beach, FL, USA<br />
                        Email:{" "}
                        <a
                            href="mailto:alberto.perez@secureidtechnologies.com"
                            className="text-indigo-400 hover:underline"
                        >
                            alberto.perez@secureidtechnologies.com
                        </a>
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">12) Changes</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        We may update this Policy periodically. Significant updates will be reflected by the
                        “Last updated” date and, where appropriate, additional notice.
                    </p>

                    <p className="mt-10 text-xs text-gray-500">
                        This Privacy Policy is provided for informational purposes and does not constitute legal advice.
                    </p>

                    <p className="mt-10 text-center lg:text-left text-sm text-gray-400">
                        <Link to="/" className="text-indigo-400 hover:underline">
                            ← Back to Home
                        </Link>
                    </p>
                </motion.div>

                {/* 🔹 Animated Image */}
                <motion.div
                    className="flex-1 flex justify-center"
                    initial={{ opacity: 0, x: 200 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                >
                    <img
                        src="/Images/privacy-secureid.png"
                        alt="Privacy and data protection illustration"
                        className="w-[90%] max-w-md rounded-2xl shadow-2xl"
                    />
                </motion.div>
            </section>
        </main>
    );
}
